import { FC, useEffect, useMemo, useState } from 'react';

import { uniq } from 'lodash';

import { Entertainer } from '@queries/EntertainerQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';

export const useEntertainerSearchFilters = ({
  initialSearchValue,
  initialCategory,
  entertainers = [],
}: {
  initialSearchValue?: string;
  initialCategory?: string;
  entertainers: Entertainer[];
}) => {
  const { track } = useAnalytics();

  const [searchValue, setSearchValue] = useState<string>(initialSearchValue);
  const [category, setCategory] = useState<string>(initialCategory);

  useEffect(() => {
    if (category || searchValue) {
      track('Search Entertainers', { category, searchValue });
    }
  }, [category, searchValue]);

  const entertainerCategories = useMemo(() => {
    const categories = entertainers.flatMap((entertainer) => entertainer.details?.categories ?? []);

    return uniq(categories).sort((a, b) => a.localeCompare(b));
  }, [entertainers]);

  const filteredEntertainers = useMemo(() => {
    const applySearchFilter = (entertainer: { title: string }) => {
      if (!searchValue) {
        return true;
      }

      const searchValueLowercased = searchValue.toLowerCase();

      return entertainer.title.toLowerCase().includes(searchValueLowercased);
    };

    const applyCategoryFilter = (entertainer: { details?: { categories: string[] } }) => {
      if (!category) {
        return true;
      }

      return entertainer?.details?.categories?.includes(category);
    };

    return entertainers.filter(applySearchFilter).filter(applyCategoryFilter) ?? [];
  }, [searchValue, category, entertainers]);

  return {
    searchValue,
    updateSearchValue: setSearchValue,
    category,
    updateCategory: setCategory,
    filteredEntertainers,
    entertainerCategories,
  };
};

type UseEntertainerSearchFilters = ReturnType<typeof useEntertainerSearchFilters>;

export const EntertainerSearchFilters: FC<UseEntertainerSearchFilters> = ({
  searchValue,
  updateSearchValue,
  category,
  updateCategory,
  entertainerCategories,
}) => {
  return (
    <div className="d-flex gap-2 mb-3">
      <div className="w-50">
        <Input
          placeholder="Search by name"
          value={searchValue}
          onChange={(e) => updateSearchValue(e.target.value)}
        />
      </div>
      <div className="w-50">
        <Select
          value={category}
          options={[
            { value: '', label: 'All Categories' },
            ...entertainerCategories.map((category) => ({
              value: category,
              label: category,
            })),
          ]}
          onChange={(event) => {
            updateCategory(event.target.value);
          }}
        />
      </div>
    </div>
  );
};
